import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useAbandonCart } from '@hooks/useAbandonCart';
import { useAnalytics } from '@hooks/useAnalytics/useAnalytics';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useCheckoutStore } from '@store/checkoutStore';
import { useConfigurationStore } from '@store/configurationStore';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { useSessionContext } from '@contexts/SessionContext';
import { startNewOrder } from '@utils/sessionUtils';

export const useStartNewOrder = () => {
  const { sessionId } = useSessionContext();
  const queryClient = useQueryClient();
  const { cart } = useShoppingCart(sessionId ?? '');
  const { abandonCart } = useAbandonCart(sessionId ?? '');
  const checkoutStore = useCheckoutStore();
  const configurationStore = useConfigurationStore();
  const { setQualificationFlow } = useQualificationFlowStore(
    useShallow((s) => ({ setQualificationFlow: s.setQualificationFlow }))
  );
  const analytics = useAnalytics();

  const [isStartingNew, setIsStartingNew] = useState(false);

  const execute = useCallback(async (): Promise<void> => {
    if (!sessionId) return;
    setIsStartingNew(true);
    try {
      await startNewOrder({
        sessionId,
        abandonCart: () => (cart?.id ? abandonCart(cart.id) : Promise.resolve()),
        resetCheckout: () => checkoutStore.reset(),
        resetConfig: () => configurationStore.resetSelections(),
        removeCartQueries: () => {
          queryClient.removeQueries({ queryKey: ['shoppingCart'] });
          queryClient.removeQueries({ queryKey: ['productOfferings'] });
          queryClient.removeQueries({ queryKey: ['existingCustomerInfo'] });
          queryClient.removeQueries({ queryKey: ['installationDetails'] });
          queryClient.removeQueries({ queryKey: ['existingPhoneOptions'] });
        },
        clearQualification: () =>
          setQualificationFlow((prev) => ({
            ...prev,
            resumeRoute: null,
            cartState: undefined,
          })),
      });
      analytics.clearLedger();
    } finally {
      setIsStartingNew(false);
    }
  }, [
    sessionId,
    cart?.id,
    abandonCart,
    checkoutStore,
    configurationStore,
    queryClient,
    setQualificationFlow,
    analytics,
  ]);

  return { startNewOrder: execute, isStartingNew };
};
